import { useEffect, useState } from 'react';

import { useAppLifecycle } from '@/hooks/useAppLifecycle';

import { getSystem, isXfinitySystem } from '@/system';

export function usePlatformState(): boolean {
  const lifecycleState = useAppLifecycle();
  const isXfinity = isXfinitySystem(getSystem());

  const [isAppInactive, setIsAppInactive] = useState(
    () =>
      isXfinity &&
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('appState') === 'inactive'
  );
  const [hasReceivedActive, setHasReceivedActive] = useState(!isXfinity);

  // Sync with global lifecycle state to handle the case where app:active was
  // dispatched before this component's event listener was registered
  // (e.g. SplashScreen mounting after router.replace('/') on foreground resume).
  useEffect(() => {
    if (lifecycleState === 'foreground') {
      setIsAppInactive(false);
      setHasReceivedActive(true);
    }
  }, [lifecycleState]);

  useEffect(() => {
    if (!isXfinity) return;

    const handleInactive = () => {
      setIsAppInactive(true);
      setHasReceivedActive(false);
    };
    const handleActive = () => {
      setIsAppInactive(false);
      setHasReceivedActive(true);
    };
    window.addEventListener('app:inactive', handleInactive);
    window.addEventListener('app:active', handleActive);
    return () => {
      window.removeEventListener('app:inactive', handleInactive);
      window.removeEventListener('app:active', handleActive);
    };
  }, [isXfinity]);

  return isAppInactive || !hasReceivedActive;
}
